import React, { useState, useEffect } from 'react';
import { Button, Input, MenuItem, Select, Checkbox, ListItemText, FormControl } from '@mui/material';
import axios from 'axios';
import arrow from '../Icons/arrow.svg';
import uploadicon from '../Icons/upload white.svg';
import removeicon from '../Icons/Close-white.svg';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../Components/Siderbar';
import Footer from '../Components/Footer';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../Customize_Css/AddVendor.css';
import '../Customize_Css/OffersForm.css';

export const AddOfferes = () => {
  const navigate = useNavigate();
  const epicxTenantId = localStorage.getItem('epicxTenantId');
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const AuthorizationToken = localStorage.getItem('token');
  const [errors, setErrors] = useState({});
  const [validFields, setValidFields] = useState({});

  // Existing state variables
  const [isActive, setIsActive] = useState(false);
  const [isAutoApply, setIsAutoApply] = useState(false);
  const [isFirstOrderOnly, setIsFirstOrderOnly] = useState(false);
  const [file, setFile] = useState(null);
  const [applicableProducts, setApplicableProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedVendorName, setSelectedVendorName] = useState('');
  const [products, setProducts] = useState([]);

  // New state variables for Display & Visibility Settings
  const [paymentMethodRestriction, setPaymentMethodRestriction] = useState('');
  const [deliveryModeRestriction, setDeliveryModeRestriction] = useState('');
  const [showDiscountOnProductPage, setShowDiscountOnProductPage] = useState(false);
  const [discountDisplayFormat, setDiscountDisplayFormat] = useState('');
  const [showOfferTagInProductList, setShowOfferTagInProductList] = useState(false);
  const [offerPriority, setOfferPriority] = useState('');

  const handleToggle = () => setIsActive(!isActive);
  const handleAutoApplyToggle = () => setIsAutoApply(!isAutoApply);
  const handleFirstOrderToggle = () => setIsFirstOrderOnly(!isFirstOrderOnly);

  const handleFileChange = (event) => setFile(event.target.files[0]);
  const handleRemoveFile = () => setFile(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/products/api/category/`,
          {
            headers: {
                Authorization: `Bearer ${AuthorizationToken}`,
              },
            }
        );
        setCategoryOptions(response.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const response = await fetch(`${BASE_URL}/vendor/api/vendor/${epicxTenantId}`,
          {
            headers: {
                Authorization: `Bearer ${AuthorizationToken}`,
              },
            }
        );
        const data = await response.json();
        if (data.vendors) setVendors(data.vendors);
        else console.error('No vendors found');
      } catch (error) {
        console.error('Error fetching vendor data:', error);
      }
    };
    fetchVendors();
  }, [epicxTenantId]);

  const fetchProductsByVendor = async (vendorId) => {
    try {
      const response = await fetch(`${BASE_URL}/products/api/productDetails/products/${vendorId}`,
        {
          headers: {
              Authorization: `Bearer ${AuthorizationToken}`,
            },
          }
      );
      const data = await response.json();
      if (response.ok && data.length > 0) setProducts(data);
      else {
        setProducts([]);
        toast.info('No products found for this vendor');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Error fetching products');
    }
  };

  const handleVendorChange = (e) => {
    const vendorId = e.target.value;
    const vendorName = vendors.find((vendor) => vendor._id === vendorId)?.vendorName || '';
    setSelectedVendor(vendorId);
    setSelectedVendorName(vendorName);
    setApplicableProducts([]);
    fetchProductsByVendor(vendorId);
  };

  const handleProductChange = (e) => setApplicableProducts(e.target.value);
  const handleCategoryChange = (e) => setCategories(e.target.value);
  const handleUserChange = (e) => setEligibleUsers(e.target.value);

  // Validation logic for individual fields
  const validateField = (id, value) => {
    const isEmpty = (val) => !val || val.toString().trim() === "";
    const isValidPositiveNumber = (val) => !isNaN(val) && Number(val) > 0;
    const isFutureDate = (date) => date && new Date(date) > new Date();
    const isEndDateValid = (endDate) => endDate && new Date(endDate) > new Date(document.getElementById("startDate")?.value || new Date());

    const validationRules = {
      offerTitle: { message: "Offer Title is required", validator: (val) => !isEmpty(val) },
      offerType: { message: "Offer Type is required", validator: (val) => !isEmpty(val) },
      offerCode: { message: "Offer Code is required", validator: (val) => !isEmpty(val) },
      discountValue: { message: "Discount Value must be a positive number", validator: isValidPositiveNumber },
      minPurchaseAmount: { message: "Minimum Purchase Amount must be a positive number", validator: isValidPositiveNumber },
      maxDiscountAmount: { message: "Maximum Discount Amount must be a positive number", validator: isValidPositiveNumber },
      startDate: { message: "Start Date must be a future date", validator: isFutureDate },
      endDate: { message: "End Date must be after Start Date", validator: isEndDateValid },
      usageLimit: { message: "Usage Limit per User must be a positive number", validator: isValidPositiveNumber },
      totalUsageLimit: { message: "Total Usage Limit must be a positive number", validator: isValidPositiveNumber },
      offerDescription: { message: "Offer Description is required", validator: (val) => !isEmpty(val) },
      eligibleUsers: { message: "Eligible Users is required", validator: (val) => !isEmpty(val) },
      offerUpload: { message: "Offer Upload file is required", validator: (val) => val !== null },
      isActive: { message: "Active/Deactivate Promo Code status is required", validator: (val) => typeof val === "boolean" },
      showDiscountOnProductPage: { message: "Show Discount on Product Page status is required", validator: (val) => typeof val === "boolean" },
      discountDisplayFormat: { message: "Discount Display Format is required", validator: (val) => !isEmpty(val) },
    };

    const rule = validationRules[id];
    if (!rule) return;

    const isValid = rule.validator(value);
    setErrors((prev) => isValid ? { ...prev, [id]: "" } : { ...prev, [id]: rule.message });
    setValidFields((prev) => isValid ? { ...prev, [id]: true } : { ...prev, [id]: false });
  };

  // Validate all fields on submit
  const validateOfferForm = () => {
    let isValid = true;
    const fieldsToValidate = [
      "offerTitle", "offerType", "offerCode", "discountValue", "minPurchaseAmount", "maxDiscountAmount",
      "startDate", "endDate", "usageLimit", "totalUsageLimit", "offerDescription", "eligibleUsers",
      "offerUpload", "isActive", "showDiscountOnProductPage", "discountDisplayFormat"
    ];

    fieldsToValidate.forEach((id) => {
      const value = id === "offerUpload" ? file : id === "isActive" ? isActive : id === "showDiscountOnProductPage" ? showDiscountOnProductPage : document.getElementById(id)?.value;
      validateField(id, value);
      if (errors[id]) isValid = false;
    });

    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateOfferForm()) return;

    const formData = new FormData();
    formData.append('offerTitle', document.getElementById('offerTitle').value);
    formData.append('offerType', document.getElementById('offerType').value);
    formData.append('offerCode', document.getElementById('offerCode').value);
    formData.append('discountValue', document.getElementById('discountValue').value);
    formData.append('minimumPurchaseAmount', document.getElementById('minPurchaseAmount').value);
    formData.append('maximumDiscountAmount', document.getElementById('maxDiscountAmount').value);
    formData.append('productVendorId', selectedVendor);
    formData.append('productVendorName', selectedVendorName || vendors.find(v => v._id === selectedVendor)?.vendorName || '');
    formData.append('applicableProducts', JSON.stringify(applicableProducts));
    formData.append('categories', JSON.stringify(categories));
    formData.append('eligibleUsers', eligibleUsers);
    formData.append('startDate', document.getElementById('startDate').value);
    formData.append('endDate', document.getElementById('endDate').value);
    formData.append('isActive', isActive);
    formData.append('isAutoApply', isAutoApply);
    formData.append('isFirstOrderOnly', isFirstOrderOnly);
    formData.append('usageLimit', document.getElementById('usageLimit').value);
    formData.append('totalUsageLimit', document.getElementById('totalUsageLimit').value);
    if (file) formData.append('offerUpload', file);
    formData.append('paymentMethodRestriction', paymentMethodRestriction);
    formData.append('deliveryModeRestriction', deliveryModeRestriction);
    formData.append('offerDescription', document.getElementById('offerDescription').value);
    formData.append('showDiscountOnProductPage', showDiscountOnProductPage);
    formData.append('discountDisplayFormat', discountDisplayFormat);
    formData.append('showOfferTagInProductList', showOfferTagInProductList);
    formData.append('offerTag', document.getElementById('offerTag').value);
    formData.append('offerPriority', document.getElementById('offerPriority').value || 0);

    try {
      const response = await fetch(`${BASE_URL}/offers/api/offers/${epicxTenantId}`, {
        headers: {
            Authorization: `Bearer ${AuthorizationToken}`,
          },
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      console.log('Success:', result);
      toast.success('Offer submitted successfully');

      // Reset form
      document.getElementById('offerTitle').value = '';
      document.getElementById('offerType').value = '';
      document.getElementById('offerCode').value = '';
      document.getElementById('discountValue').value = '';
      document.getElementById('minPurchaseAmount').value = '';
      document.getElementById('maxDiscountAmount').value = '';
      document.getElementById('startDate').value = '';
      document.getElementById('endDate').value = '';
      document.getElementById('usageLimit').value = '';
      document.getElementById('totalUsageLimit').value = '';
      document.getElementById('offerDescription').value = '';
      document.getElementById('offerTag').value = '';
      document.getElementById('offerPriority').value = '';
      setApplicableProducts([]);
      setCategories([]);
      setEligibleUsers('');
      setSelectedVendor('');
      setIsActive(false);
      setIsAutoApply(false);
      setIsFirstOrderOnly(false);
      setFile(null);
      setPaymentMethodRestriction('');
      setDeliveryModeRestriction('');
      setShowDiscountOnProductPage(false);
      setDiscountDisplayFormat('');
      setShowOfferTagInProductList(false);
      setOfferPriority('');
      setErrors({});
      setValidFields({});
    } catch (error) {
      console.error('Error:', error);
      // toast.error(`Error: ${error.message || 'Something went wrong'}`);
    }
  };

  return (
    <>
      <Sidebar />
      <div className="vendor-form-page">
        <div className="vendor-form-shell">
          <header className="vendor-form-hero">
            <div>
              <p className="vendor-form-kicker">Sales</p>
              <h1>Add Offers</h1>
              <p>Set the offer title, discount, dates, products, and visibility. Required fields stay the same.</p>
            </div>
            <button type="button" className="vendor-form-back-btn" onClick={() => navigate('/view-offer')}>
              Back to Offers
            </button>
          </header>
          <div>
            <div className="vendor-form-card">
              <div className="vendor-form-card-head">
                <div>
                  <h2>Offers Information</h2>
                  <p>Discount rules, dates, products, and who can use this offer.</p>
                </div>
              </div>
              <div className="vendor-form-card-body">
                <div className="row">
                  <div className="col-md-4">
                    <label htmlFor="offerTitle">Offer Title</label>
                    <span style={{ color: 'red' }}>*</span>
                    <input
                      type="text"
                      className={`form-control bg-light ${errors.offerTitle ? 'is-invalid' : validFields.offerTitle ? 'is-valid' : ''}`}
                      id="offerTitle"
                      onBlur={(e) => validateField('offerTitle', e.target.value)}
                    />
                    {errors.offerTitle && <small className="text-danger">{errors.offerTitle}</small>}
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="offerType">Offer Type</label>
                    <span style={{ color: 'red' }}>*</span>
                    <select
                      className={`form-control bg-light ${errors.offerType ? 'is-invalid' : validFields.offerType ? 'is-valid' : ''}`}
                      id="offerType"
                      onChange={(e) => validateField('offerType', e.target.value)}
                      style={{
                        backgroundImage: `url(${arrow})`,
                        backgroundPosition: 'right 10px center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: '16px 16px',
                        paddingRight: '30px',
                        color: 'gray',
                      }}
                    >
                      <option value="">Select Offer Type</option>
                      <option value="Flat-Discount">Flat Discount</option>
                      <option value="Percentage">Percentage</option>
                      <option value="buyOneGetOne">Buy One Get One</option>
                    </select>
                    {errors.offerType && <small className="text-danger">{errors.offerType}</small>}
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="productVendor">Product Vendor Name</label>
                    <select
                      id="productVendor"
                      className="form-select bg-light"
                      value={selectedVendor}
                      onChange={handleVendorChange}
                      style={{ color: 'gray' }}
                    >
                      <option value="">Select Product Vendor</option>
                      {vendors.length > 0 ? (
                        vendors.map((vendor) => (
                          <option key={vendor._id} value={vendor._id}>
                            {`${vendor.vendorName} (${vendor.emailAddress})`}
                          </option>
                        ))
                      ) : (
                        <option disabled>No vendors available</option>
                      )}
                    </select>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-4">
                    <label htmlFor="offerCode">Offer Code</label>
                    <span style={{ color: 'red' }}>*</span>
                    <input
                      type="text"
                      className={`form-control bg-light ${errors.offerCode ? 'is-invalid' : validFields.offerCode ? 'is-valid' : ''}`}
                      id="offerCode"
                      onBlur={(e) => validateField('offerCode', e.target.value)}
                    />
                    {errors.offerCode && <small className="text-danger">{errors.offerCode}</small>}
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="discountValue">Discount Value</label>
                    <span style={{ color: 'red' }}>*</span>
                    <input
                      type="number"
                      className={`form-control bg-light ${errors.discountValue ? 'is-invalid' : validFields.discountValue ? 'is-valid' : ''}`}
                      id="discountValue"
                      onBlur={(e) => validateField('discountValue', e.target.value)}
                    />
                    {errors.discountValue && <small className="text-danger">{errors.discountValue}</small>}
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="minPurchaseAmount">Minimum Purchase Amount</label>
                    <span style={{ color: 'red' }}>*</span>
                    <input
                      type="number"
                      className={`form-control bg-light ${errors.minPurchaseAmount ? 'is-invalid' : validFields.minPurchaseAmount ? 'is-valid' : ''}`}
                      id="minPurchaseAmount"
                      onBlur={(e) => validateField('minPurchaseAmount', e.target.value)}
                    />
                    {errors.minPurchaseAmount && <small className="text-danger">{errors.minPurchaseAmount}</small>}
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-4">
                    <label htmlFor="maxDiscountAmount">Maximum Discount Amount</label>
                    <span style={{ color: 'red' }}>*</span>
                    <input
                      type="number"
                      className={`form-control bg-light ${errors.maxDiscountAmount ? 'is-invalid' : validFields.maxDiscountAmount ? 'is-valid' : ''}`}
                      id="maxDiscountAmount"
                      onBlur={(e) => validateField('maxDiscountAmount', e.target.value)}
                    />
                    {errors.maxDiscountAmount && <small className="text-danger">{errors.maxDiscountAmount}</small>}
                  </div>
                  <div className="col">
                    <label htmlFor="startDate">Start Date</label>
                    <span style={{ color: 'red' }}>*</span>
                    <input
                      type="date"
                      className={`form-control bg-light ${errors.startDate ? 'is-invalid' : validFields.startDate ? 'is-valid' : ''}`}
                      id="startDate"
                      onBlur={(e) => validateField('startDate', e.target.value)}
                    />
                    {errors.startDate && <small className="text-danger">{errors.startDate}</small>}
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="endDate">End Date</label>
                    <span style={{ color: 'red' }}>*</span>
                    <input
                      type="date"
                      className={`form-control bg-light ${errors.endDate ? 'is-invalid' : validFields.endDate ? 'is-valid' : ''}`}
                      id="endDate"
                      onBlur={(e) => validateField('endDate', e.target.value)}
                    />
                    {errors.endDate && <small className="text-danger">{errors.endDate}</small>}
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-4">
                    <label htmlFor="applicableProducts">Applicable Products</label>
                    <FormControl fullWidth className="offer-multi">
                      <Select
                        labelId="applicableProducts-label"
                        id="applicableProducts"
                        multiple
                        displayEmpty
                        value={applicableProducts}
                        onChange={handleProductChange}
                        MenuProps={{ PaperProps: { className: 'offer-select-menu' } }}
                        renderValue={(selected) => (
                          selected.length === 0 ? <span className="offer-placeholder">Choose products</span> : (
                          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                            {selected.map((value) => (
                              <span className="offer-chip" key={value}>{value}</span>
                            ))}
                          </div>
                          )
                        )}
                      >
                        {products.length > 0 ? (
                          products.map((product) => (
                            <MenuItem key={product._id} value={product.productName}>
                              <Checkbox checked={applicableProducts.indexOf(product.productName) > -1} />
                              <ListItemText primary={product.productName} />
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem disabled>No products available</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="categories">Categories</label>
                    <FormControl fullWidth className="offer-multi">
                    <Select
                      id="categories"
                      multiple
                      displayEmpty
                      value={categories}
                      onChange={handleCategoryChange}
                      MenuProps={{ PaperProps: { className: 'offer-select-menu' } }}
                      renderValue={(selected) => (
                        selected.length === 0 ? <span className="offer-placeholder">Choose categories</span> : (
                        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                          {selected.map((value) => (
                            <span className="offer-chip" key={value}>{value}</span>
                          ))}
                        </div>
                        )
                      )}
                    >
                      {categoryOptions.map((category) => (
                        <MenuItem key={category.id} value={category.categoryName}>
                          <Checkbox checked={categories.indexOf(category.categoryName) > -1} />
                          <ListItemText primary={category.categoryName} />
                        </MenuItem>
                      ))}
                    </Select>
                    </FormControl>
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="eligibleUsers">Eligible Users</label>
                    <select
                      id="eligibleUsers"
                      value={eligibleUsers}
                      onChange={(e) => {
                        handleUserChange(e);
                        validateField('eligibleUsers', e.target.value);
                      }}
                      className={`form-select bg-light ${errors.eligibleUsers ? 'is-invalid' : validFields.eligibleUsers ? 'is-valid' : ''}`}
                      style={{ color: 'gray' }}
                    >
                      <option value="">Select an option</option>
                      <option value="new_user">New User</option>
                      <option value="existing_user">Existing User</option>
                      <option value="all">All</option>
                    </select>
                    {errors.eligibleUsers && <small className="text-danger">{errors.eligibleUsers}</small>}
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-4">
                    <label>Active or Deactivate Promo Code</label>
                    <span style={{ color: 'red' }}>*</span>
                    <div className="form-check form-switch" style={{ marginLeft: '25px' }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="promoCodeToggle"
                        checked={isActive}
                        onChange={() => {
                          handleToggle();
                          validateField('isActive', !isActive);
                        }}
                        style={{
                          backgroundColor: isActive ? '#01B074' : '',
                          fontSize: '25px',
                          border: isActive ? 'none' : '',
                          outline: isActive ? 'none' : '',
                        }}
                      />
                      <label className="form-check-label" htmlFor="promoCodeToggle">
                        {isActive ? 'Active' : 'Deactivate'}
                      </label>
                    </div>
                    {errors.isActive && <small className="text-danger">{errors.isActive}</small>}
                  </div>
                  <div className="col-md-4">
                    <label>Auto-Apply Offer</label>
                    <div className="form-check form-switch" style={{ marginLeft: '25px' }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="autoApplyToggle"
                        checked={isAutoApply}
                        onChange={handleAutoApplyToggle}
                        style={{
                          backgroundColor: isAutoApply ? '#01B074' : '',
                          fontSize: '25px',
                          border: isAutoApply ? 'none' : '',
                          outline: isAutoApply ? 'none' : '',
                        }}
                      />
                      <label className="form-check-label" htmlFor="autoApplyToggle">
                        {isAutoApply ? 'Active' : 'Deactivate'}
                      </label>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label>First Order Only</label>
                    <div className="form-check form-switch" style={{ marginLeft: '25px' }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="firstOrderToggle"
                        checked={isFirstOrderOnly}
                        onChange={handleFirstOrderToggle}
                        style={{
                          backgroundColor: isFirstOrderOnly ? '#01B074' : '',
                          fontSize: '25px',
                          border: isFirstOrderOnly ? 'none' : '',
                          outline: isFirstOrderOnly ? 'none' : '',
                        }}
                      />
                      <label className="form-check-label" htmlFor="firstOrderToggle">
                        {isFirstOrderOnly ? 'Active' : 'Deactivate'}
                      </label>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-4">
                    <label htmlFor="usageLimit">Usage Limit per User</label>
                    <span style={{ color: 'red' }}>*</span>
                    <input
                      type="number"
                      className={`form-control bg-light ${errors.usageLimit ? 'is-invalid' : validFields.usageLimit ? 'is-valid' : ''}`}
                      id="usageLimit"
                      onBlur={(e) => validateField('usageLimit', e.target.value)}
                    />
                    {errors.usageLimit && <small className="text-danger">{errors.usageLimit}</small>}
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="totalUsageLimit">Total Usage Limit</label>
                    <span style={{ color: 'red' }}>*</span>
                    <input
                      type="number"
                      className={`form-control bg-light ${errors.totalUsageLimit ? 'is-invalid' : validFields.totalUsageLimit ? 'is-valid' : ''}`}
                      id="totalUsageLimit"
                      onBlur={(e) => validateField('totalUsageLimit', e.target.value)}
                    />
                    {errors.totalUsageLimit && <small className="text-danger">{errors.totalUsageLimit}</small>}
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="file-upload-button">Offer Upload</label>
                    <span style={{ color: 'red' }}>*</span>
                    <div className="form-group">
                      <label htmlFor="file-upload-button" style={{ display: 'block' }}>
                        <Input
                          accept="image/*"
                          id="file-upload-button"
                          type="file"
                          onChange={(e) => {
                            handleFileChange(e);
                            validateField('offerUpload', e.target.files[0]);
                          }}
                          style={{ display: 'none' }}
                        />
                        <Button
                          variant="contained"
                          component="span"
                          className="vendor-field-action"
                          endIcon={
                            file ? (
                              <img src={removeicon} alt="Remove Icon" style={{ width: '20px', height: '20px' }} />
                            ) : (
                              <img src={uploadicon} alt="Upload Icon" style={{ width: '20px', height: '20px' }} />
                            )
                          }
                          onClick={file ? () => {
                            handleRemoveFile();
                            validateField('offerUpload', null);
                          } : null}
                        >
                          {file ? file.name : 'Upload'}
                        </Button>
                      </label>
                    </div>
                    {errors.offerUpload && <small className="text-danger">{errors.offerUpload}</small>}
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-4">
                    <label htmlFor="paymentMethodRestriction">Payment Method Restriction</label>
                    <select
                      id="paymentMethodRestriction"
                      value={paymentMethodRestriction}
                      onChange={(e) => setPaymentMethodRestriction(e.target.value)}
                      className="form-control bg-light"
                    >
                      <option value="">None</option>
                      <option value="wallet">Only for Wallet Payments</option>
                      <option value="card">Only for Card Payments</option>
                      <option value="cod">Only for Cash on Delivery</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="deliveryModeRestriction">Delivery Mode Restriction</label>
                    <select
                      id="deliveryModeRestriction"
                      value={deliveryModeRestriction}
                      onChange={(e) => setDeliveryModeRestriction(e.target.value)}
                      className="form-control bg-light"
                    >
                      <option value="">None</option>
                      <option value="delivery">Valid for Delivery Orders Only</option>
                      <option value="pickup">Valid for Pickup Orders Only</option>
                    </select>
                  </div>
                  <div className="col">
                    <label htmlFor="offerDescription">Offer Code Description</label>
                    <span style={{ color: 'red' }}>*</span>
                    <textarea
                      className={`form-control bg-light ${errors.offerDescription ? 'is-invalid' : validFields.offerDescription ? 'is-valid' : ''}`}
                      id="offerDescription"
                      rows="2"
                      placeholder="Enter Offer Description Here"
                      onBlur={(e) => validateField('offerDescription', e.target.value)}
                    ></textarea>
                    {errors.offerDescription && <small className="text-danger">{errors.offerDescription}</small>}
                  </div>
                </div>
              </div>
            </div>

            {/* Display & Visibility Settings Card */}
            <div className="vendor-form-card">
              <div className="vendor-form-card-head">
                <div>
                  <h2>Display & Visibility Settings</h2>
                  <p>Choose how this offer appears on product pages.</p>
                </div>
              </div>
              <div className="vendor-form-card-body">
                <div className="row">
                  <div className="col-md-4">
                    <label>Show Discount on Product Page?</label>
                    <span style={{ color: 'red' }}>*</span>
                    <div className="form-check form-switch" style={{ marginLeft: '25px' }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={showDiscountOnProductPage}
                        onChange={() => {
                          setShowDiscountOnProductPage(!showDiscountOnProductPage);
                          validateField('showDiscountOnProductPage', !showDiscountOnProductPage);
                        }}
                        style={{
                          backgroundColor: showDiscountOnProductPage ? '#01B074' : '',
                        }}
                      />
                      <label className="form-check-label">
                        {showDiscountOnProductPage ? 'Yes' : 'No'}
                      </label>
                    </div>
                    {errors.showDiscountOnProductPage && <small className="text-danger">{errors.showDiscountOnProductPage}</small>}
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="discountDisplayFormat">Discount Display Format</label>
                    <span style={{ color: 'red' }}>*</span>
                    <select
                      id="discountDisplayFormat"
                      value={discountDisplayFormat}
                      onChange={(e) => {
                        setDiscountDisplayFormat(e.target.value);
                        validateField('discountDisplayFormat', e.target.value);
                      }}
                      className={`form-control bg-light ${errors.discountDisplayFormat ? 'is-invalid' : validFields.discountDisplayFormat ? 'is-valid' : ''}`}
                    >
                      <option value="">Select Format</option>
                      <option value="percentage">Show Percentage (-20%)</option>
                      <option value="price">Show Discounted Price ($8.00)</option>
                      <option value="both">Show Both</option>
                    </select>
                    {errors.discountDisplayFormat && <small className="text-danger">{errors.discountDisplayFormat}</small>}
                  </div>
                  <div className="col-md-4">
                    <label>Show Offer Tag in Product List?</label>
                    <div className="form-check form-switch" style={{ marginLeft: '25px' }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={showOfferTagInProductList}
                        onChange={() => setShowOfferTagInProductList(!showOfferTagInProductList)}
                        style={{
                          backgroundColor: showOfferTagInProductList ? '#01B074' : '',
                        }}
                      />
                      <label className="form-check-label">
                        {showOfferTagInProductList ? 'Yes' : 'No'}
                      </label>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="offerTag">Offer Tag</label>
                    <input type="text" className="form-control bg-light" id="offerTag" />
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="offerPriority">Offer Priority</label>
                    <input
                      type="number"
                      className="form-control bg-light"
                      id="offerPriority"
                      value={offerPriority}
                      onChange={(e) => setOfferPriority(e.target.value)}
                      placeholder="Enter priority (e.g., 1 for highest)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="vendor-form-actions">
            <button type="button" className="vendor-form-cancel" onClick={() => navigate('/view-offer')}>Cancel</button>
            <button type="button" className="vendor-form-submit" onClick={handleSubmit}>Save and Submit</button>
          </div>
        </div>
        <div className="vendor-form-footer">
          <Footer />
        </div>
        <ToastContainer
          position="top-center"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={true}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          style={{ zIndex: 9999 }}
        />
      </div>
    </>
  );
};